import React, { useState } from 'react';
import type { Portfolio } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';

interface PortfolioManagerProps {
  portfolios: Portfolio[];
  currentPortfolioId: string;
  onSwitchPortfolio: (portfolioId: string) => void;
  onCreatePortfolio: (name: string) => Promise<string>;
  onDeletePortfolio: (portfolioId: string) => void;
  onRenamePortfolio: (portfolioId: string, newName: string) => void;
  isUnlocked: boolean;
  onUnlock: () => Promise<boolean>;
  currency: string;
}

declare global {
  interface Window {
    bsvPayments?: {
      requestPayment: (action: string, amount: number) => Promise<any>;
      getPaymentAmount: (action: string) => number;
    };
  }
}

export default function PortfolioManager({
  portfolios,
  currentPortfolioId,
  onSwitchPortfolio,
  onCreatePortfolio,
  onDeletePortfolio,
  onRenamePortfolio,
  isUnlocked,
  onUnlock,
  currency
}: PortfolioManagerProps) {
  const [showManager, setShowManager] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlock = async () => {
    setUnlocking(true);
    setError(null);
    try {
      const success = await onUnlock();
      if (success) {
        setShowManager(true);
      } else {
        setError('Connect MetaNet wallet');
      }
    } catch (err) {
      console.error('Error unlocking multiple portfolios:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setUnlocking(false);
    }
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      setError('Please enter a portfolio name.');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await onCreatePortfolio(newPortfolioName.trim());
      setNewPortfolioName('');
      setShowManager(false);
    } catch (err) {
      console.error('Error creating portfolio:', err);
      setError('Failed to create portfolio. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleStartRename = (portfolio: Portfolio) => {
    setRenamingId(portfolio.id);
    setRenameValue(portfolio.name);
  };

  const handleSaveRename = (portfolioId: string) => {
    if (!renameValue.trim()) {
      setError('Portfolio name cannot be empty.');
      return;
    }
    onRenamePortfolio(portfolioId, renameValue.trim());
    setRenamingId(null);
    setRenameValue('');
    setError(null);
  };

  const calculatePortfolioValue = (portfolio: Portfolio): number => {
    return portfolio.items.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.currentPrice || 0),
      0
    );
  };

  if (!isUnlocked) {
    return (
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 mb-8 shadow-bubble border-2 border-primary/10">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
              💼 Portfolio Manager
            </h3>
            <p className="text-text-secondary text-sm">
              Create multiple portfolios to organize your investments (10,000 satoshis per additional portfolio)
            </p>
          </div>
          <button
            onClick={handleUnlock}
            className="px-8 py-4 bg-gradient-to-r from-primary to-purple-500 border-none rounded-full text-white text-base font-bold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95"
            disabled={unlocking}
          >
            {unlocking ? 'Processing...' : '🔓 Unlock (10,000 sats)'}
          </button>
        </div>
        {error && <p className="text-red-400 mt-3 text-sm">{error}</p>}
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 shadow-bubble border-2 border-primary/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            💼 Portfolio Manager
          </h3>
          <button
            onClick={() => setShowManager(!showManager)}
            className="px-6 py-3 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-sm font-semibold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95"
          >
            {showManager ? 'Hide Manager' : 'Manage Portfolios'}
          </button>
        </div>

        {/* Portfolio Selector */}
        <div className="mb-4">
          <label className="block mb-2 text-text-secondary font-bold uppercase tracking-wider text-xs">
            Active Portfolio:
          </label>
          <select
            value={currentPortfolioId}
            onChange={(e) => onSwitchPortfolio(e.target.value)}
            className="w-full px-6 py-3 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20"
          >
            {portfolios.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.items.length} coins, {formatCurrency(calculatePortfolioValue(p), currency)})
              </option>
            ))}
          </select>
        </div>

        {showManager && (
          <div className="mt-6 space-y-4">
            {/* Create New Portfolio */}
            <div className="bg-bg p-6 rounded-2xl border-2 border-border">
              <h4 className="text-lg font-bold mb-4 text-text">Create New Portfolio</h4>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Portfolio name..."
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreatePortfolio()}
                  className="flex-1 px-6 py-3 bg-bg-secondary border-2 border-border rounded-full text-text text-base transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20"
                />
                <button
                  onClick={handleCreatePortfolio}
                  disabled={creating || !newPortfolioName.trim()}
                  className="px-8 py-3 bg-gradient-to-r from-success to-green-400 border-none rounded-full text-white text-base font-bold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>

            {/* Portfolio List */}
            <div className="bg-bg p-6 rounded-2xl border-2 border-border">
              <h4 className="text-lg font-bold mb-4 text-text">Your Portfolios</h4>
              {portfolios.length === 0 ? (
                <p className="text-text-secondary">No portfolios yet. Create one above!</p>
              ) : (
                <div className="space-y-3">
                  {portfolios.map(portfolio => {
                    const value = calculatePortfolioValue(portfolio);
                    const isActive = portfolio.id === currentPortfolioId;
                    return (
                      <div
                        key={portfolio.id}
                        className={`p-4 rounded-xl border-2 ${
                          isActive ? 'border-primary bg-primary/10' : 'border-border bg-bg-secondary'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            {renamingId === portfolio.id ? (
                              <div className="flex gap-2 items-center">
                                <input
                                  type="text"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleSaveRename(portfolio.id);
                                    if (e.key === 'Escape') {
                                      setRenamingId(null);
                                      setRenameValue('');
                                    }
                                  }}
                                  className="px-4 py-2 bg-bg border-2 border-border rounded-lg text-text"
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleSaveRename(portfolio.id)}
                                  className="px-4 py-2 bg-success text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setRenamingId(null);
                                    setRenameValue('');
                                  }}
                                  className="px-4 py-2 bg-bg border-2 border-border rounded-lg text-text text-sm font-semibold hover:bg-bg-secondary transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-3">
                                  <h5 className="text-lg font-bold text-text">{portfolio.name}</h5>
                                  {isActive && (
                                    <span className="px-3 py-1 bg-primary text-white rounded-full text-xs font-bold">
                                      Active
                                    </span>
                                  )}
                                </div>
                                <p className="text-text-secondary text-sm mt-1">
                                  {portfolio.items.length} coins • {formatCurrency(value, currency)}
                                </p>
                              </>
                            )}
                          </div>
                          {renamingId !== portfolio.id && (
                            <div className="flex gap-2">
                              {!isActive && (
                                <>
                                  <button
                                    onClick={() => onSwitchPortfolio(portfolio.id)}
                                    className="px-4 py-2 bg-primary/20 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors"
                                  >
                                    Switch
                                  </button>
                                  <button
                                    onClick={() => handleStartRename(portfolio)}
                                    className="px-4 py-2 bg-bg border-2 border-border rounded-lg text-text text-sm font-semibold hover:bg-bg-secondary transition-colors"
                                  >
                                    Rename
                                  </button>
                                  <button
                                    onClick={() => onDeletePortfolio(portfolio.id)}
                                    className="px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-semibold hover:bg-danger/30 transition-colors"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                              {isActive && (
                                <button
                                  onClick={() => handleStartRename(portfolio)}
                                  className="px-4 py-2 bg-bg border-2 border-border rounded-lg text-text text-sm font-semibold hover:bg-bg-secondary transition-colors"
                                >
                                  Rename
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

